import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Route, 
  Users, 
  UserCheck, 
  Calendar, 
  Truck, 
  Settings,
  Menu,
  X,
  LogOut,
  Bell,
  BarChart3,
  Navigation,
  Clock,
  Shield,
  MousePointer2,
  FlaskConical,
  Activity,
  Ticket,
  ClipboardList,
  DollarSign,
  Briefcase,
  Percent,
  Banknote,
  Scale,
  Sparkles,
  UserPlus,
} from 'lucide-react'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Avatar,
  Badge,
  Divider,
  useBreakpointValue,
  Container,
} from '@chakra-ui/react'
import { useAuthStore } from '@store/authStore'
import { useQuery } from 'react-query'
import { cmsService } from '@services/api'
import NotificationBell from '@components/NotificationBell'

interface LayoutProps {
  children: React.ReactNode
}

type NavItem = { name: string; href: string; icon: any; adminOnly?: boolean; operatorOnly?: boolean }

const allNavigation: NavItem[] = [
  { name: 'Control Center', href: '/control', icon: Sparkles, adminOnly: true },
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Routes', href: '/routes', icon: Route },
  { name: 'Drivers', href: '/drivers', icon: UserCheck },
  { name: 'Applications', href: '/applications', icon: ClipboardList },
  { name: 'Jobs', href: '/jobs', icon: Briefcase },
  { name: 'Commuters', href: '/passengers', icon: Ticket },
  { name: 'Bookings', href: '/bookings', icon: Calendar },
  { name: 'Pricing', href: '/pricing', icon: DollarSign },
  { name: 'Tickets', href: '/tickets', icon: Ticket },
  { name: 'Vehicles', href: '/vehicles', icon: Truck },
  { name: 'Trips', href: '/trips', icon: Navigation },
  { name: 'Timetables', href: '/timetables', icon: Clock },
  { name: 'Partner requests', href: '/partner-requests', icon: UserPlus, adminOnly: true },
  { name: 'Operators', href: '/operators', icon: Shield, adminOnly: true },
  { name: 'Payouts', href: '/payouts', icon: Banknote, adminOnly: true },
  { name: 'Money check', href: '/reconciliation', icon: Scale, adminOnly: true },
  { name: 'Commission', href: '/commission', icon: Percent, adminOnly: true },
  { name: 'Broadcasts', href: '/notifications', icon: Bell },
  { name: 'User Insights', href: '/insights', icon: MousePointer2 },
  { name: 'LATRA Activity', href: '/latra-activity', icon: Activity, adminOnly: true },
  { name: 'LATRA Test Unit', href: '/latra-test', icon: FlaskConical, adminOnly: true },
  { name: 'Settings', href: '/settings', icon: Settings },
]

export default function Layout({ children }: LayoutProps) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const isMobile = useBreakpointValue({ base: true, lg: false })

  const displayName = user?.firstName 
    ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`
    : user?.email || user?.phoneNumber || 'Admin User';

  // Fetch owner dashboard settings
  const { data: ownerSettings } = useQuery(
    'owner-dashboard-settings',
    () => cmsService.getOwnerDashboardSettings(),
    { enabled: user?.role === 'operator', staleTime: 300000, retry: false }
  )

  const allowedTabs: string[] = ownerSettings?.data?.allowedTabs || ['Dashboard', 'Analytics', 'Vehicles', 'Drivers', 'Timetables', 'Trips', 'Bookings', 'Applications', 'Jobs']

  // Filter navigation based on role
  // Companies always get their money pages; staff-only pages never reach them (the API enforces this too).
  const navigation = user?.role === 'operator'
    ? allNavigation.filter(item => !item.adminOnly && (item.operatorOnly || allowedTabs.includes(item.name)))
    : allNavigation.filter(item => !item.operatorOnly && (!item.adminOnly || user?.role === 'admin'));

  const isActive = (href: string) => {
    return location.pathname === href
  }

  const SidebarContent = ({ onClose: closeSidebar }: { onClose?: () => void }) => (
    <VStack h="full" spacing={0}>
      {/* Logo */}
      <Flex h="16" w="full" align="center" px={6} borderBottom="1px solid" borderColor="gray.100">
        <HStack spacing={3}>
          <Box
            w="10"
            h="10"
            bgGradient="linear(to-br, brand.500, brand.600)"
            borderRadius="xl"
            display="flex"
            alignItems="center"
            justifyContent="center"
            shadow="lg"
          >
            <Text fontSize="lg" fontWeight="bold" color="white">
              DS
            </Text>
          </Box>
          <VStack spacing={0} align="start" display={{ base: 'none', xl: 'flex' }}>
            <Text fontSize="lg" fontWeight="bold" color="gray.900">
              Daladala Smart
            </Text>
            <Text fontSize="xs" color="gray.500">
              Management Portal
            </Text>
          </VStack>
          <VStack spacing={0} align="start" display={{ base: 'flex', xl: 'none' }}>
            <Text fontSize="md" fontWeight="bold" color="gray.900">
              DS
            </Text>
            <Text fontSize="xs" color="gray.500">
              Portal
            </Text>
          </VStack>
        </HStack>
      </Flex>

      {/* Navigation */}
      <VStack
        flex="1"
        w="full"
        spacing={1}
        px={4}
        py={6}
        overflowY="auto"
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bg: 'gray.200', borderRadius: 'full' },
        }}
      >
              {navigation.map((item) => {
                const Icon = item.icon
          const active = isActive(item.href)
                return (
            <Button
              key={item.name}
              as={Link}
                      to={item.href}
              w="full"
              justifyContent="flex-start"
              variant="ghost"
              leftIcon={<Icon size={20} />}
              colorScheme={active ? 'brand' : 'gray'}
              bg={active ? 'brand.50' : 'transparent'}
              color={active ? 'brand.700' : 'gray.700'}
              border={active ? '1px solid' : 'none'}
              borderColor={active ? 'brand.200' : 'transparent'}
              shadow={active ? 'sm' : 'none'}
              onClick={closeSidebar}
              _hover={{
                bg: active ? 'brand.50' : 'gray.50',
                color: active ? 'brand.700' : 'gray.900',
                transform: 'translateX(2px)',
              }}
              size="md"
              borderRadius="xl"
              transition="all 0.2s ease"
              fontSize="sm"
              fontWeight={active ? 'semibold' : 'medium'}
            >
                      {item.name}
            </Button>
                )
              })}
      </VStack>

          {/* User section */}
      <Box w="full" p={4} borderTop="1px solid" borderColor="gray.200">
        <HStack spacing={3}>
          <Avatar
            size="sm"
            bg="brand.100"
            color="brand.700"
            name={displayName}
          />
          <VStack flex="1" align="start" spacing={0} display={{ base: 'none', xl: 'flex' }}>
            <Text fontSize="sm" fontWeight="medium" color="gray.900" noOfLines={1}>
                  {displayName}
            </Text>
            <Text fontSize="xs" color="gray.500" textTransform="capitalize">
              {user?.role || 'admin'}
            </Text>
          </VStack>
          <IconButton
            aria-label="Logout"
            icon={<LogOut size={16} />}
            size="sm"
            variant="ghost"
            color="gray.400"
                onClick={logout}
            _hover={{ bg: 'gray.100', color: 'gray.600' }}
            borderRadius="xl"
          />
        </HStack>
      </Box>
    </VStack>
  )

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody p={0}>
            <SidebarContent onClose={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Desktop Sidebar */}
      <Box
        display={{ base: 'none', lg: 'block' }}
        position="fixed"
        left={0}
        top={0}
        w={{ base: '64', xl: '72' }}
        h="100vh"
        bg="white"
        shadow="lg"
        zIndex={10}
        borderRight="1px solid"
        borderColor="gray.100"
      >
        <SidebarContent />
      </Box>

      {/* Main Content */}
      <Box ml={{ base: 0, lg: '64', xl: '72' }}>
        {/* Top Bar */}
        <Flex
          h="16"
          align="center"
          justify="space-between"
          bg="white"
          px={{ base: 4, md: 6, lg: 8 }}
          shadow="sm"
          position="sticky"
          top={0}
          zIndex={5}
        >
          {/* Left side - Mobile menu button */}
          <Box>
            <IconButton
              aria-label="Open menu"
              icon={<Menu size={24} />}
              variant="ghost"
              color="gray.500"
              display={{ base: 'flex', lg: 'none' }}
              onClick={onOpen}
              _hover={{ color: 'gray.600', bg: 'gray.50' }}
              borderRadius="xl"
            />
          </Box>
          
          {/* Right side - User info and notifications */}
          <HStack spacing={{ base: 2, md: 4 }}>
            {/* Notifications: the real inbox for staff */}
            {user?.role === 'admin' ? <NotificationBell /> : (
              <Box position="relative">
                <IconButton
                  as={Link}
                  to="/notifications"
                  aria-label="Notifications"
                  icon={<Bell size={20} />}
                  variant="ghost"
                  color="gray.500"
                  _hover={{ bg: 'gray.100', color: 'gray.600' }}
                  borderRadius="xl"
                />
              </Box>
            )}
            
            {/* User Profile */}
            <HStack spacing={3} display={{ base: 'none', sm: 'flex' }}>
              <Avatar
                size="sm"
                bg="brand.100"
                color="brand.700"
                name={displayName}
              />
              <VStack align="start" spacing={0} display={{ base: 'none', md: 'flex' }}>
                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                    {displayName}
                </Text>
                <Text fontSize="xs" color="gray.500" textTransform="capitalize">
                  {user?.role || 'admin'}
                </Text>
              </VStack>
            </HStack>
          </HStack>
        </Flex>

        {/* Page Content */}
        <Box px={{ base: 4, md: 6, lg: 8 }} py={{ base: 4, md: 6, lg: 8 }}>
          <Container maxW="container.xl" px={0}>
            {children}
          </Container>
        </Box>
      </Box>
    </Box>
  )
}
